/**
 * Ensures an image path is a valid path.
 *
 * @param imagePath - Path to an image represented as a URL or relative path.
 *
 * @returns - Returns true if an image path is a valid path.
 *
 */
export const isValidPreviewImage = (imagePath: string | undefined): boolean => {
  if (!imagePath) {
    return false;
  }
  if (!isValidRelativePath(imagePath) && !isValidURL(imagePath)) {
    return false;
  }

  const fileName = imagePath.split('/').pop();
  if (!fileName) {
    return false;
  }
  return isValidImageExtension(fileName);
};

/**
 * Determines if URL is valid.
 *
 * @param url - URL as string.
 *
 * @returns - Returns true if a URL is valid.
 *
 */
export const isValidURL = (url: string | undefined): boolean => {
  if (!url) {
    return false;
  }
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Determines if a filepath is a valid relative path.
 *
 * @param filePath - A file path.
 *
 * @returns - Returns if a filepath is a valid relative path.
 *
 */
export const isValidRelativePath = (filePath: string | undefined): boolean => {
  if (!filePath) {
    return false;
  }
  return filePath.startsWith('/');
};

const validImageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'svg'];

/**
 * Determines if a file name has a valid image extension.
 *
 * @param fileName - A file name.
 *
 * @returns - Returns if a file name has a valid image extension.
 *
 */
export const isValidImageExtension = (fileName: string): boolean => {
  const extension = fileName.split('.').pop();
  if (!extension) {
    return false;
  }
  return validImageExtensions.includes(extension);
};

const hexColorCodeRegex = new RegExp(/^#(?:[0-9A-Fa-f]{3}){1,2}$/i);

/**
 * Determines if a color is a valid hex code.
 *
 * @param color - Color as a hex code.
 *
 * @returns - Returns if a color is a valid hex code.
 *
 */
export const isValidHexCode = (color: string | undefined): boolean => {
  if (!color) {
    return false;
  }
  return hexColorCodeRegex.test(color);
};

const uuidRegex = new RegExp(
  /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/
);

/**
 * Determine if a string is a valid UUID
 * @param uuid - UUID string
 * @returns boolean
 */
export const isValidUUID = (uuid: string | null): boolean => {
  if (!uuid) {
    return false;
  }
  return uuidRegex.test(uuid);
};
